import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Field, Input, Textarea } from '@/ui/Field';
import { SelectorDeIcono } from '@/components/SelectorDeIcono';
import { useGuardarPlantilla, type Plantilla } from './api';

/**
 * FormularioDePlantilla — el nombre, el ícono y a quién le llega el correo.
 *
 * Los campos sobre el PDF **no** se configuran aquí: eso es su propia pantalla,
 * con dirección propia, porque necesita el documento delante.
 */
export function FormularioDePlantilla({
  plantilla, onCerrar, onCreada,
}: {
  plantilla: Plantilla | null;
  onCerrar: () => void;
  /** Una plantilla nueva nace sin PDF, así que lo siguiente es configurarla. */
  onCreada?: (id: number) => void;
}) {
  const guardar = useGuardarPlantilla();
  const [nombre, setNombre] = useState(plantilla?.name ?? '');
  const [descripcion, setDescripcion] = useState(plantilla?.description ?? '');
  const [correos, setCorreos] = useState(plantilla?.recipient_emails ?? '');
  const [icono, setIcono] = useState(plantilla?.icon || 'bi-file-earmark-text');
  const [error, setError] = useState('');

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v && !guardar.isPending) onCerrar(); }}
      titulo={plantilla ? 'Editar la plantilla' : 'Nueva plantilla'}
      descripcion="El PDF oficial y la posición de los campos se configuran después, sobre el documento."
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button
            loading={guardar.isPending}
            onClick={() => {
              if (!nombre.trim()) { setError('Escribe el nombre de la plantilla.'); return; }
              guardar.mutate(
                {
                  id: plantilla?.id ?? null,
                  cuerpo: {
                    name: nombre.trim(),
                    description: descripcion.trim() || null,
                    recipient_emails: correos.trim() || null,
                    icon: icono || 'bi-file-earmark-text',
                  },
                },
                {
                  onSuccess: (r) => {
                    Utils.toast(plantilla ? 'Plantilla actualizada' : 'Plantilla creada', 'success');
                    onCerrar();
                    const id = (r as { id?: number }).id;
                    if (!plantilla && id && onCreada) onCreada(id);
                  },
                  onError: (err) => setError(
                    err instanceof Error ? err.message : 'No se pudo guardar'),
                },
              );
            }}
          >
            <i className="bi bi-check-lg" aria-hidden="true" /> Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 pb-2">
        <Field label="Nombre" error={error || undefined}>
          <Input
            value={nombre}
            onChange={(e) => { setNombre(e.target.value); setError(''); }}
            placeholder="Ej: Certificado laboral"
          />
        </Field>
        <Field label="Ícono">
          <SelectorDeIcono
            id="tpl-icono" valor={icono} onCambio={setIcono} respaldo="bi-file-earmark-text"
          />
        </Field>
        <Field label="Descripción" opcional>
          <Textarea rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </Field>
        <Field
          label="Correos de Bienestar"
          opcional
          ayuda="Separados por coma. Ahí llega el PDF radicado. Sin ninguno, se usa el ADMIN_EMAIL del servidor."
        >
          <Input
            value={correos}
            onChange={(e) => setCorreos(e.target.value)}
            placeholder="bienestar@empresa.com"
          />
        </Field>
      </div>
    </Dialog>
  );
}
